import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Plus, Trash2, TrendingUp, Calculator, FileText, ChevronDown, X, ArrowRight, Check } from 'lucide-react';
import api from '../../services/api';

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const BRL = (v) => v != null ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const PCT = (v) => v != null ? `${Number(v).toFixed(4)}%` : '—';

function Tab({ label, icon: Icon, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      <Icon size={16} />{label}
    </button>
  );
}

// ─── Aba: Índices INCC ────────────────────────────────────────────────────────
function AbaIndices() {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState('incc'); // incc (INCC-DI, até o habite-se) | igpm (IGPM-FGV, pós-habite-se)
  const [anoFiltro, setAnoFiltro] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ ano: '', mes: '', valor_percentual: '' });
  const [showForm, setShowForm] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  const { data: indices = [], isLoading } = useQuery({
    queryKey: ['incc-indices', tipo, anoFiltro],
    queryFn: () => api.get('/incc/indices', { params: { tipo, ano: anoFiltro } }).then(r => r.data.data),
  });

  const sync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await api.post('/incc/indices/sync', { tipo, ano_inicio: anoFiltro - 2 }, { timeout: 30000 });
      const { inseridos, atualizados } = r.data.data;
      setSyncMsg(`✓ ${inseridos} novos + ${atualizados} atualizados`);
      qc.invalidateQueries(['incc-indices', tipo]);
    } catch (e) {
      setSyncMsg('✗ ' + (e.response?.data?.error || 'Erro ao sincronizar'));
    } finally { setSyncing(false); }
  };

  const salvarManual = useMutation({
    mutationFn: () => api.post('/incc/indices', { ...form, tipo }),
    onSuccess: () => { qc.invalidateQueries(['incc-indices', tipo]); setShowForm(false); setForm({ ano: '', mes: '', valor_percentual: '' }); },
  });

  const anos = [];
  for (let y = new Date().getFullYear(); y >= 2018; y--) anos.push(y);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button onClick={() => setTipo('incc')}
          className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${tipo === 'incc' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}>
          INCC-DI <span className="font-normal text-xs">(até o habite-se)</span>
        </button>
        <button onClick={() => setTipo('igpm')}
          className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${tipo === 'igpm' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}>
          IGPM-FGV <span className="font-normal text-xs">(após o habite-se)</span>
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <select value={anoFiltro} onChange={e => setAnoFiltro(parseInt(e.target.value))}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {anos.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={sync} disabled={syncing}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Sincronizando...' : 'Sincronizar com BCB'}
        </button>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
          <Plus size={14} /> Inserir manualmente
        </button>
        {syncMsg && <span className={`text-sm font-medium ${syncMsg.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>{syncMsg}</span>}
      </div>

      {showForm && (
        <form onSubmit={e => { e.preventDefault(); salvarManual.mutate(); }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ano</label>
            <input type="number" required placeholder="2025" value={form.ano} onChange={e => setForm(f => ({ ...f, ano: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mês (1-12)</label>
            <input type="number" min="1" max="12" required placeholder="4" value={form.mes} onChange={e => setForm(f => ({ ...f, mes: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{tipo === 'igpm' ? 'IGPM' : 'INCC'} % (ex: 0.4800)</label>
            <input type="number" step="0.0001" required placeholder="0.4800" value={form.valor_percentual} onChange={e => setForm(f => ({ ...f, valor_percentual: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button type="submit" disabled={salvarManual.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {salvarManual.isPending ? 'Salvando...' : 'Salvar'}
          </button>
          <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-100 px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
          <span>Mês</span><span>Ano</span><span>{tipo === 'igpm' ? 'IGPM-FGV' : 'INCC-DI'} (%)</span><span>Fonte</span>
        </div>
        {isLoading ? <div className="text-center py-8 text-gray-400">Carregando...</div> :
          indices.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p className="font-medium">Nenhum índice para {anoFiltro}</p>
              <p className="text-sm mt-1">Clique em "Sincronizar com BCB" para importar os dados</p>
            </div>
          ) : indices.map(idx => (
            <div key={idx.id} className="grid grid-cols-4 px-4 py-2.5 border-b border-gray-50 text-sm hover:bg-gray-50">
              <span className="font-medium">{MESES[idx.mes - 1]}</span>
              <span>{idx.ano}</span>
              <span className="text-blue-700 font-semibold">{PCT(idx.valor_percentual)}</span>
              <span className="text-gray-400 text-xs">{idx.fonte}</span>
            </div>
          ))
        }
      </div>
      <p className="text-xs text-gray-400">
        Fonte: BCB/SGS Série {tipo === 'igpm' ? '189 (IGP-M)' : '192 (INCC-DI)'}. Sincronização automática disponível.
      </p>
    </div>
  );
}

// ─── Modal: Novo / Editar Contrato ────────────────────────────────────────────
function ModalContrato({ empreendimentos, contrato, onClose, onSuccess }) {
  const [form, setForm] = useState(contrato ? {
    empreendimento_id: contrato.empreendimento_id || '',
    cliente_nome: contrato.cliente_nome,
    cliente_cpf: contrato.cliente_cpf,
    unidade: contrato.unidade,
    bloco: contrato.bloco || '',
    saldo_devedor_base: contrato.saldo_devedor_base,
    saldo_devedor_atual: contrato.saldo_devedor_atual,
    data_base: contrato.data_base?.split('T')[0] || '',
    total_parcelas: contrato.total_parcelas,
    parcelas_pagas: contrato.parcelas_pagas,
    valor_parcela_base: contrato.valor_parcela_base || '',
    data_habite_se: contrato.data_habite_se?.split('T')[0] || '',
    observacoes: contrato.observacoes || '',
  } : {
    empreendimento_id: '', cliente_nome: '', cliente_cpf: '', unidade: '', bloco: '',
    saldo_devedor_base: '', saldo_devedor_atual: '', data_base: '', total_parcelas: '',
    parcelas_pagas: '0', valor_parcela_base: '', data_habite_se: '', observacoes: '',
  });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setErro('');
    try {
      if (contrato) {
        await api.put(`/incc/contratos/${contrato.id}`, form);
      } else {
        const payload = { ...form };
        if (!payload.saldo_devedor_atual) payload.saldo_devedor_atual = payload.saldo_devedor_base;
        await api.post('/incc/contratos', payload);
      }
      onSuccess();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-800">{contrato ? 'Editar Contrato INCC' : 'Novo Contrato INCC'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Empreendimento</label>
              <select value={form.empreendimento_id} onChange={set('empreendimento_id')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Selecione...</option>
                {empreendimentos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Nome do cliente *</label>
              <input required value={form.cliente_nome} onChange={set('cliente_nome')} placeholder="Nome completo"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">CPF *</label>
              <input required value={form.cliente_cpf} onChange={set('cliente_cpf')} placeholder="000.000.000-00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Bloco</label>
              <input value={form.bloco} onChange={set('bloco')} placeholder="A, B, Torre 1..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Unidade *</label>
              <input required value={form.unidade} onChange={set('unidade')} placeholder="101, 202..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Saldo devedor base (V₀) *</label>
              <input required type="number" step="0.01" value={form.saldo_devedor_base} onChange={set('saldo_devedor_base')} placeholder="100000.00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            {contrato && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Saldo devedor atual</label>
                <input type="number" step="0.01" value={form.saldo_devedor_atual} onChange={set('saldo_devedor_atual')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Data base (T₀) *</label>
              <input required type="date" value={form.data_base} onChange={set('data_base')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Total de parcelas *</label>
              <input required type="number" min="1" value={form.total_parcelas} onChange={set('total_parcelas')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Parcelas já pagas</label>
              <input type="number" min="0" value={form.parcelas_pagas} onChange={set('parcelas_pagas')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Parcela base inicial</label>
              <input type="number" step="0.01" value={form.valor_parcela_base} onChange={set('valor_parcela_base')} placeholder="Opcional"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Data do Habite-se</label>
              <input type="date" value={form.data_habite_se} onChange={set('data_habite_se')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Observações</label>
              <textarea rows={2} value={form.observacoes} onChange={set('observacoes')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
          </div>
          {erro && <p className="text-red-500 text-sm">{erro}</p>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Salvando...' : contrato ? 'Atualizar' : 'Cadastrar contrato'}
            </button>
            <button type="button" onClick={onClose} className="px-4 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detalhe do contrato ───────────────────────────────────────────────────────
function DetalheContrato({ id, onBack }) {
  const qc = useQueryClient();
  const [aplicando, setAplicando] = useState(false);
  const [msg, setMsg] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['incc-calcular', id],
    queryFn: () => api.get(`/incc/contratos/${id}/calcular`).then(r => r.data.data),
  });

  const aplicar = async () => {
    if (!window.confirm('Confirma a aplicação do reajuste INCC para este mês?')) return;
    setAplicando(true); setMsg('');
    try {
      await api.post(`/incc/contratos/${id}/aplicar`);
      setMsg('✓ Reajuste aplicado com sucesso!');
      refetch();
      qc.invalidateQueries(['incc-contratos']);
    } catch (err) {
      setMsg('✗ ' + (err.response?.data?.error || 'Erro ao aplicar reajuste'));
    } finally { setAplicando(false); }
  };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Carregando...</div>;
  if (!data) return null;
  const { contrato, parcelas_restantes, saldo_devedor_atual, incc_referencia, proximo_saldo_devedor, proximo_valor_parcela, proximo_incc_percentual, historico, indice_usado, juros_percentual } = data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div>
          <h2 className="font-bold text-gray-900">{contrato.cliente_nome}</h2>
          <p className="text-xs text-gray-400">CPF: {contrato.cliente_cpf} · Unid. {contrato.bloco ? `${contrato.bloco}-` : ''}{contrato.unidade}</p>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Saldo Devedor Atual', value: BRL(saldo_devedor_atual), color: 'blue' },
          { label: 'Parcelas Restantes', value: `${parcelas_restantes} de ${contrato.total_parcelas}`, color: 'gray' },
          { label: `${indice_usado || 'INCC-DI'} ref. ${incc_referencia ? `${MESES[incc_referencia.mes-1]}/${incc_referencia.ano}` : '—'}`, value: incc_referencia ? PCT(incc_referencia.percentual) : 'N/D', color: 'yellow' },
          { label: 'Próxima Parcela', value: BRL(proximo_valor_parcela), color: 'green' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`bg-${color}-50 border border-${color}-100 rounded-xl p-4`}>
            <p className={`text-xs text-${color}-600 font-medium mb-1`}>{label}</p>
            <p className={`text-lg font-bold text-${color}-900`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Próximo reajuste */}
      {contrato.status === 'ativo' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-blue-600" /> Próximo Reajuste</h3>
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <div><span className="text-gray-500">Saldo atual:</span> <strong>{BRL(saldo_devedor_atual)}</strong></div>
            <ArrowRight size={16} className="text-gray-400" />
            <div><span className="text-gray-500">+ {indice_usado || 'INCC-DI'} {PCT(proximo_incc_percentual)}{juros_percentual > 0 ? ` (inclui ${PCT(juros_percentual)} de juros)` : ''}:</span> <strong className="text-blue-700">{BRL(proximo_saldo_devedor)}</strong></div>
            <ArrowRight size={16} className="text-gray-400" />
            <div><span className="text-gray-500">Parcela:</span> <strong className="text-green-700">{BRL(proximo_valor_parcela)}</strong></div>
          </div>
          {contrato.data_habite_se && new Date() > new Date(contrato.data_habite_se) && (
            <p className="text-xs text-blue-600 mt-2">Contrato pós-habite-se: correção por {indice_usado} + juros de {PCT(juros_percentual)} ao mês (Cláusula Sexta, Parágrafo Sexto).</p>
          )}
          {msg && <p className={`text-sm mt-3 font-medium ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>{msg}</p>}
          <button onClick={aplicar} disabled={aplicando || !incc_referencia}
            className="mt-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            <Check size={14} /> {aplicando ? 'Aplicando...' : 'Aplicar reajuste deste mês'}
          </button>
          {!incc_referencia && <p className="text-xs text-orange-600 mt-2">Índice {indice_usado || 'INCC-DI'} n-2 não encontrado. Sincronize os índices primeiro.</p>}
        </div>
      )}

      {/* Histórico */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <h3 className="font-semibold text-gray-800 px-5 py-3 border-b border-gray-100">Histórico de Reajustes</h3>
        {historico.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">Nenhum reajuste aplicado ainda</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Competência</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Índice Ref.</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Juros</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Saldo Anterior</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Saldo Novo</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Parcela</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {historico.map(h => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{MESES[new Date(h.competencia).getUTCMonth()]}/{new Date(h.competencia).getUTCFullYear()}</td>
                  <td className="px-4 py-2 text-blue-700">{h.indexador || 'INCC-DI'} {PCT(h.incc_percentual)}</td>
                  <td className="px-4 py-2 text-gray-500">{h.juros_percentual > 0 ? PCT(h.juros_percentual) : '—'}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{BRL(h.saldo_anterior)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{BRL(h.saldo_apos_reajuste)}</td>
                  <td className="px-4 py-2 text-right text-green-700 font-semibold">{BRL(h.valor_parcela)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Aba: Contratos ───────────────────────────────────────────────────────────
function AbaContratos() {
  const qc = useQueryClient();
  const [detalheId, setDetalheId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('ativo');

  const { data: contratos = [], isLoading } = useQuery({
    queryKey: ['incc-contratos', filtroStatus],
    queryFn: () => api.get('/incc/contratos', { params: { status: filtroStatus || undefined } }).then(r => r.data.data),
  });

  const { data: empreendimentos = [] } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data || r.data),
  });

  const deletar = useMutation({
    mutationFn: (id) => api.delete(`/incc/contratos/${id}`),
    onSuccess: () => qc.invalidateQueries(['incc-contratos']),
  });

  if (detalheId) return <DetalheContrato id={detalheId} onBack={() => setDetalheId(null)} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Todos</option>
          <option value="ativo">Ativos</option>
          <option value="entregue">Entregues (Habite-se)</option>
          <option value="encerrado">Encerrados</option>
        </select>
        <button onClick={() => { setEditando(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">
          <Plus size={14} /> Novo contrato
        </button>
      </div>

      {isLoading ? <div className="text-center py-12 text-gray-400">Carregando...</div> :
        contratos.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-gray-500 font-medium">Nenhum contrato cadastrado</p>
            <p className="text-gray-400 text-sm mt-1">Clique em "Novo contrato" para começar</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Empreend.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Unidade</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Saldo Atual</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Parcelas</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contratos.map(c => (
                  <tr key={c.id} onClick={() => setDetalheId(c.id)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{c.cliente_nome}</p>
                      <p className="text-xs text-gray-400">{c.cliente_cpf}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{c.empreendimento_nome || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.bloco ? `${c.bloco}-` : ''}{c.unidade}</td>
                    <td className="px-4 py-3 text-right font-semibold text-blue-700">{BRL(c.saldo_devedor_atual)}</td>
                    <td className="px-4 py-3 text-center text-gray-500 hidden lg:table-cell">
                      {c.parcelas_pagas}/{c.total_parcelas}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                        <button onClick={() => { setEditando(c); setShowModal(true); }}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                          <FileText size={14} />
                        </button>
                        <button onClick={() => { if (window.confirm('Excluir este contrato?')) deletar.mutate(c.id); }}
                          className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {showModal && (
        <ModalContrato empreendimentos={empreendimentos} contrato={editando}
          onClose={() => { setShowModal(false); setEditando(null); }}
          onSuccess={() => { setShowModal(false); setEditando(null); qc.invalidateQueries(['incc-contratos']); }} />
      )}
    </div>
  );
}

// ─── Aba: Calculadora ─────────────────────────────────────────────────────────
function AbaCalculadora() {
  const [form, setForm] = useState({ saldo_devedor: '', total_parcelas: '', parcelas_pagas: '0', meses_simular: '12' });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const calcular = async (e) => {
    e.preventDefault(); setLoading(true); setErro(''); setResultado(null);
    try {
      const r = await api.post('/incc/calculadora', form, { timeout: 30000 });
      setResultado(r.data.data);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao calcular');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Calculator size={16} className="text-blue-600" /> Simulação de Reajuste INCC</h3>
        <form onSubmit={calcular} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Saldo Devedor Atual</label>
            <input required type="number" step="0.01" placeholder="100000.00" value={form.saldo_devedor} onChange={set('saldo_devedor')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Total Parcelas</label>
            <input required type="number" min="1" placeholder="60" value={form.total_parcelas} onChange={set('total_parcelas')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pagas</label>
            <input type="number" min="0" placeholder="0" value={form.parcelas_pagas} onChange={set('parcelas_pagas')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Meses a simular</label>
            <input type="number" min="1" max="60" value={form.meses_simular} onChange={set('meses_simular')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {erro && <p className="col-span-4 text-red-500 text-sm">{erro}</p>}
          <div className="col-span-2 sm:col-span-4">
            <button type="submit" disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Calculando...' : 'Simular projeção'}
            </button>
          </div>
        </form>
      </div>

      {resultado && resultado.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-800">Projeção de Reajustes</h3>
            <p className="text-xs text-gray-400 mt-0.5">Usando INCC-DI histórico (n-2). Meses futuros podem não ter índice disponível.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Mês</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase">INCC</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Saldo Anterior</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Saldo Novo</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Parcela</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Restam</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {resultado.map((r, i) => (
                  <tr key={i} className={i === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-2 font-medium">{MESES[r.mes - 1]}/{r.ano}</td>
                    <td className="px-4 py-2 text-center text-blue-700">{r.incc_percentual != null ? PCT(r.incc_percentual) : <span className="text-orange-500 text-xs">N/D</span>}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{BRL(r.saldo_anterior)}</td>
                    <td className="px-4 py-2 text-right font-semibold">{r.saldo_novo != null ? BRL(r.saldo_novo) : '—'}</td>
                    <td className="px-4 py-2 text-right text-green-700 font-semibold">{r.valor_parcela != null ? BRL(r.valor_parcela) : '—'}</td>
                    <td className="px-4 py-2 text-center text-gray-500">{r.parcelas_restantes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ReajusteINCC() {
  const [aba, setAba] = useState('contratos');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reajuste INCC</h1>
        <p className="text-gray-500 text-sm mt-1">Gestão de contratos corrigidos pelo INCC-DI (FGV via BCB)</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200 px-2">
          <Tab label="Contratos" icon={FileText} active={aba === 'contratos'} onClick={() => setAba('contratos')} />
          <Tab label="Índices INCC" icon={TrendingUp} active={aba === 'indices'} onClick={() => setAba('indices')} />
          <Tab label="Calculadora" icon={Calculator} active={aba === 'calculadora'} onClick={() => setAba('calculadora')} />
        </div>
        <div className="p-5">
          {aba === 'contratos' && <AbaContratos />}
          {aba === 'indices' && <AbaIndices />}
          {aba === 'calculadora' && <AbaCalculadora />}
        </div>
      </div>
    </div>
  );
}
